import Seo from '@/components/public/Seo';
import PageHero from '@/components/public/sections/PageHero';
import { parishName, useSite } from '@/hooks/useSite';
import { trackUmamiEvent } from '@/lib/analytics';

const sectionLabel =
  'text-xs font-bold tracking-widest uppercase text-primary \
  mb-3 block';

// Fixed RGPD policy. The legal text is the same for every parish; only the
// parish-specific references (name, seat/address, contacts) react to the
// configured parish via useSite().
export default function Privacy() {
  const site = useSite();
  const name = parishName(site);
  const hq = site.locations.find((l) => l.headquarters) ?? site.locations[0];
  const baseClassName = 'max-w-7xl mx-auto px-4 md:px-8 py-16';

  return (
    <>
      <Seo
        title="Privacidade"
        description={`Informação sobre o tratamento dos seus dados pessoais por ${name}, nos termos do RGPD.`}
      />
      <PageHero
        badge="Proteção de dados"
        breadcrumbs={[{ label: 'Início', href: '/' }, { label: 'Política de Privacidade' }]}
        title="Política de Privacidade."
        accentWord="Privacidade."
        subtitle={`Informação sobre o tratamento dos seus dados pessoais por ${name}, nos termos do Regulamento Geral sobre a Proteção de Dados (RGPD).`}
      />
      <div className={baseClassName}>
        <div className="mx-auto max-w-4xl">
          <article className="surface-card text-on-surface-light space-y-8 p-6 leading-relaxed md:p-10">
            <section>
              <span className={sectionLabel}>1 — Responsável pelo tratamento</span>
              <h2 className="font-headline text-on-surface mb-3 text-xl font-bold">
                Quem trata os seus dados
              </h2>
              <p>
                {name}
                {hq?.address ? `, com sede em ${hq.address},` : ''} é a entidade responsável pelo
                tratamento dos dados pessoais recolhidos através deste sítio Web.
              </p>
              <p className="mt-3">
                Para qualquer questão relativa ao tratamento dos seus dados, contacte-nos
                {site.contactEmail ? (
                  <>
                    {' '}
                    por correio eletrónico em{' '}
                    <a
                      href={`mailto:${site.contactEmail}`}
                      className="text-primary font-medium hover:underline"
                    >
                      {site.contactEmail}
                    </a>
                  </>
                ) : null}
                {hq?.phone ? (
                  <>
                    {site.contactEmail ? ' ou' : ''} pelo telefone{' '}
                    <span className="text-on-surface font-medium">{hq.phone}</span>
                  </>
                ) : null}
                .
              </p>
            </section>

            <hr className="border-line" />

            <section>
              <span className={sectionLabel}>2 — Dados recolhidos</span>
              <h2 className="font-headline text-on-surface mb-3 text-xl font-bold">
                Que dados recolhemos
              </h2>
              <p>
                Recolhemos apenas os dados estritamente necessários para tratar cada pedido.
                Consoante o serviço, isso pode incluir:
              </p>
              <ul className="mt-3 ml-5 list-outside list-disc space-y-2">
                <li>
                  Dados de identificação: nome, data de nascimento, NIF, NISS, morada, contactos
                  (correio eletrónico e telefone).
                </li>
                <li>
                  Dados sobre a sua situação familiar, laboral ou económica (apenas quando solicita
                  apoio social).
                </li>
                <li>
                  Documentos comprovativos estritamente necessários que anexe aos formulários. A
                  reprodução do Cartão de Cidadão só será solicitada quando exista fundamento legal
                  ou consentimento nos termos aplicáveis.
                </li>
                <li>
                  Dados sobre eventos e atividades para licenciamento de ruído ou reserva de
                  espaços.
                </li>
                <li>
                  Dados relativos a denúncias submetidas através do canal interno, incluindo factos
                  comunicados, elementos de prova, contacto facultativo e metadados necessários à
                  tramitação legal.
                </li>
              </ul>
            </section>

            <hr className="border-line" />

            <section>
              <span className={sectionLabel}>3 — Finalidades</span>
              <h2 className="font-headline text-on-surface mb-3 text-xl font-bold">
                Para que tratamos os dados
              </h2>
              <p>Os seus dados são tratados exclusivamente para:</p>
              <ul className="mt-3 ml-5 list-outside list-disc space-y-2">
                <li>
                  Emissão de atestados, declarações e outros documentos, no exercício de atribuições
                  de autoridade pública e no cumprimento das obrigações legais aplicáveis.
                </li>
                <li>
                  Análise e atribuição de apoios sociais (exercício de funções de interesse público
                  ou de autoridade pública).
                </li>
                <li>
                  Resposta a pedidos de informação, exposições ou reclamações, no exercício de
                  funções de interesse público ou no cumprimento de obrigações legais, conforme
                  aplicável.
                </li>
                <li>Reserva de espaços e equipamentos comunitários (execução contratual).</li>
              </ul>
            </section>

            <hr className="border-line" />

            <section>
              <span className={sectionLabel}>4 — Conservação</span>
              <h2 className="font-headline text-on-surface mb-3 text-xl font-bold">
                Por quanto tempo guardamos os dados
              </h2>
              <p>
                Os dados são conservados apenas pelo período necessário ao cumprimento das
                finalidades acima descritas e pelos prazos legalmente exigidos para arquivo
                administrativo. Findos esses prazos, os dados são eliminados ou anonimizados de
                forma segura.
              </p>
            </section>

            <hr className="border-line" />

            <section>
              <span className={sectionLabel}>5 — Partilha</span>
              <h2 className="font-headline text-on-surface mb-3 text-xl font-bold">
                Com quem partilhamos
              </h2>
              <p>
                Os seus dados não são vendidos nem cedidos a terceiros para fins comerciais. Podem
                ser partilhados, exclusivamente quando estritamente necessário, com:
              </p>
              <ul className="mt-3 ml-5 list-outside list-disc space-y-2">
                <li>
                  A Câmara Municipal{' '}
                  {site.municipality ? `de ${site.municipality}` : 'do respetivo concelho'}, no
                  âmbito de processos de competência partilhada.
                </li>
                <li>
                  Serviços da Administração Pública (Segurança Social, Autoridade Tributária,
                  Conservatórias) quando legalmente exigido.
                </li>
                <li>
                  Subcontratantes técnicos (alojamento, email), sempre com acordos de proteção de
                  dados.
                </li>
              </ul>
            </section>

            <hr className="border-line" />

            <section>
              <span className={sectionLabel}>6 — Direitos do titular</span>
              <h2 className="font-headline text-on-surface mb-3 text-xl font-bold">
                Os seus direitos
              </h2>
              <p>
                Nos termos do RGPD e dentro dos limites legalmente aplicáveis, pode exercer os
                seguintes direitos:
              </p>
              <ul className="mt-3 ml-5 list-outside list-disc space-y-2">
                <li>Direito de acesso aos seus dados pessoais.</li>
                <li>Direito de retificação de dados incorretos ou incompletos.</li>
                <li>
                  Direito ao apagamento («direito ao apagamento»), salvo quando a conservação for
                  legalmente obrigatória.
                </li>
                <li>Direito à limitação ou oposição ao tratamento.</li>
                <li>Direito à portabilidade dos dados, quando aplicável.</li>
                <li>
                  Direito de retirar o consentimento a qualquer momento, sem prejuízo da licitude
                  dos tratamentos anteriores.
                </li>
              </ul>
              <p className="mt-3">
                Para exercer qualquer destes direitos, contacte-nos pelos meios indicados no ponto
                1. Tem ainda o direito de apresentar reclamação à Comissão Nacional de Proteção de
                Dados (CNPD) em{' '}
                <a
                  href="https://www.cnpd.pt"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() =>
                    trackUmamiEvent('external_link_click', {
                      target: 'https://www.cnpd.pt',
                      label: 'CNPD',
                      source: 'privacy',
                    })
                  }
                  className="text-primary font-medium hover:underline"
                >
                  www.cnpd.pt
                </a>
                .
              </p>
            </section>

            <hr className="border-line" />

            <section>
              <span className={sectionLabel}>7 — Segurança</span>
              <h2 className="font-headline text-on-surface mb-3 text-xl font-bold">
                Como protegemos os dados
              </h2>
              <p>
                Adotamos medidas técnicas e organizativas adequadas à proteção dos seus dados contra
                perda, acesso não autorizado, alteração ou divulgação, incluindo a transmissão
                cifrada (HTTPS) e o controlo de acessos.
              </p>
            </section>

            <hr className="border-line" />

            <section>
              <span className={sectionLabel}>8 — Atualizações</span>
              <h2 className="font-headline text-on-surface mb-3 text-xl font-bold">
                Alterações a esta política
              </h2>
              <p>
                Esta política pode ser revista para refletir alterações legais ou operacionais. A
                versão em vigor encontra-se sempre disponível nesta página.
              </p>
            </section>
          </article>
        </div>
      </div>
    </>
  );
}
